window.AdminUsers = function AdminUsers({ user: currentUser, showNotification }) {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [form, setForm] = React.useState({ id: 0, username: '', email: '', password: '', full_name: '', role: 'admin', status: 'active' });

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('users.php');
      setUsers(res.data || []);
    } catch (e) {
      setUsers([]);
      showNotification(e.message || 'Unable to load users from the database.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setIsEditing(false);
    setForm({ id: 0, username: '', email: '', password: '', full_name: '', role: 'admin', status: 'active' });
    setShowModal(true);
  };

  const openEditModal = (u) => {
    setIsEditing(true);
    setForm({ id: u.id, username: u.username, email: u.email, password: '', full_name: u.full_name, role: u.role, status: u.status || 'active' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await window.apiClient.put('users.php', form);
      } else {
        await window.apiClient.post('users.php', form);
      }
      showNotification(`User '${form.username}' ${isEditing ? 'updated' : 'created'} successfully.`);
      setShowModal(false);
      await fetchUsers();
    } catch (e) {
      showNotification(e.message || 'Unable to save user to the database.', 'error');
    }
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Delete user '${username}'?`)) return;
    try {
      await window.apiClient.delete('users.php', { id });
      showNotification(`User '${username}' deleted.`);
      await fetchUsers();
    } catch (e) {
      showNotification(e.message || 'Unable to delete user from the database.', 'error');
    }
  };

  return (
    <>
      <div className="space-y-6 animate-fadeIn">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="font-serif text-2xl font-bold text-slate-900">Administrator User Management</h1>
          <p className="font-sans text-xs text-slate-500">Manage administrator accounts, assign roles and set permissions.</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold uppercase px-5 py-3 rounded-lg shadow transition-all flex items-center gap-2 cursor-pointer"
        >
          <span className="material-symbols-outlined text-lg">person_add</span>
          <span>Add Admin User</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative w-full sm:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
          <input
            type="text"
            placeholder="Search users by username, email or full name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs outline-none focus:border-secondary transition-colors"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <table className="w-full text-left text-xs font-sans">
          <thead className="bg-slate-50 text-slate-500 font-mono text-[10px] uppercase border-b border-slate-200">
            <tr>
              <th className="p-4">USER</th>
              <th className="p-4">EMAIL ADDRESS</th>
              <th className="p-4">ROLE</th>
              <th className="p-4">STATUS</th>
              <th className="p-4 text-center">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-slate-400 font-mono text-xs">No administrator accounts found.</td>
              </tr>
            ) : (
              users
                .filter(u => {
                  if (!u) return false;
                  const query = searchQuery.toLowerCase();
                  return (
                    (u.username || '').toLowerCase().includes(query) ||
                    (u.email || '').toLowerCase().includes(query) ||
                    (u.full_name || '').toLowerCase().includes(query)
                  );
                })
                .map((u) => (
                <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4">
                    <div className="font-bold text-slate-900 text-sm font-sans">{u.full_name}</div>
                    <div className="text-slate-500 font-mono text-[11px]">@{u.username}</div>
                  </td>
                  <td className="p-4 font-mono text-slate-700">{u.email}</td>
                  <td className="p-4">
                    <span className="font-mono text-[10px] uppercase bg-orange-50 text-secondary border border-orange-200 px-2 py-0.5 rounded font-bold">
                      {u.role}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded ${
                      u.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {u.status || 'active'}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEditModal(u)} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 rounded cursor-pointer" title="Edit User">
                        <span className="material-symbols-outlined text-sm">edit</span>
                      </button>
                      <button onClick={() => handleDelete(u.id, u.username)} disabled={u.id === currentUser?.id} className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded disabled:opacity-30 cursor-pointer" title="Delete User">
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
            {users.length > 0 && users.filter(u => {
              const query = searchQuery.toLowerCase();
              return (
                (u.username || '').toLowerCase().includes(query) ||
                (u.email || '').toLowerCase().includes(query) ||
                (u.full_name || '').toLowerCase().includes(query)
              );
            }).length === 0 && (
              <tr>
                <td colSpan="5" className="p-8 text-center text-slate-400 font-mono">No administrator accounts match your search.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

      {/* Modal - uses inline styles to guarantee viewport centering */}
      {showModal && (
        <div
          style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(15,23,42,0.5)',backdropFilter:'blur(4px)',padding:'16px'}}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div style={{background:'#fff',borderRadius:'16px',border:'1px solid #e2e8f0',boxShadow:'0 25px 50px -12px rgba(0,0,0,0.25)',width:'100%',maxWidth:'672px',padding:'24px'}}>
            
            {/* Modal Header */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid #f1f5f9',paddingBottom:'12px',marginBottom:'16px'}}>
              <div>
                <h3 className="font-serif text-xl font-bold text-slate-900">
                  {isEditing ? 'Edit Administrator Account' : 'Create New Administrator Account'}
                </h3>
                <p className="text-xs text-slate-500 font-mono mt-1">Assign user credentials and admin access privileges</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-800 p-1 cursor-pointer" style={{border:'none',background:'none'}}>
                <span className="material-symbols-outlined text-xl">close</span>
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:'16px'}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
                <div>
                  <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">Full Name *</label>
                  <input type="text" required value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} placeholder="e.g. Alexander Vance" className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary text-xs" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">Username *</label>
                  <input type="text" required value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} placeholder="e.g. alex_vance" className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary text-xs font-mono" />
                </div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
                <div>
                  <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">Email Address *</label>
                  <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="alex@novatek.co.in" className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary text-xs" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">{isEditing ? 'New Password' : 'Password *'}</label>
                  <input type="password" required={!isEditing} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder={isEditing ? 'Leave blank to keep' : '••••••••'} className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary text-xs font-mono" />
                </div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
                <div>
                  <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">Administrator Role</label>
                  <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary text-xs font-mono">
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="content_editor">Content Editor</option>
                  </select>
                </div>
                <div>
                  <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">Account Status</label>
                  <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary text-xs font-mono">
                    <option value="active">Active Access</option>
                    <option value="inactive">Suspended / Inactive</option>
                  </select>
                </div>
              </div>

              <div style={{display:'flex',justifyContent:'flex-end',gap:'12px',paddingTop:'12px',borderTop:'1px solid #f1f5f9'}}>
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-mono rounded text-xs cursor-pointer">
                  Cancel
                </button>
                <button type="submit" className="px-6 py-2 bg-secondary hover:bg-orange-600 text-white font-mono font-bold rounded shadow cursor-pointer text-xs transition-all">
                  {isEditing ? 'Save Changes' : 'Create Admin User'}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </>
  );
};
